import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import API_URL from '../../config/api';
import { useAuth } from '../../context/AuthContext';
import { apiFetch } from '../../utils/apiFetch';
import { PageLayout } from '../../components/DesignSystem';
import { Button, Tag } from 'antd';

interface Order {
  id: string | number;
  customerName?: string;
  customer_name?: string;
  order_number?: string;
  items: {
    productName?: string;
    product_name?: string;
    qty?: number;
    quantity?: number;
    price?: number;
    unit_price?: number;
  }[];
  status: string;
  orderDate?: string;
  order_date?: string;
  total: number;
  has_backordered?: boolean;
}

const statusColor = (status: string) => {
  if (status === 'Completed') return 'success';
  if (status === 'Pending') return 'warning';
  if (status === 'Processing') return 'processing';
  return 'default';
};

const CustomerOrders: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    if (!id) return;

    setLoading(true);
    apiFetch(`${API_URL}/orders?customer_id=${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => (res.ok ? res.json() : []))
      .then(data => setOrders(Array.isArray(data) ? data : []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  return (
    <PageLayout
      title={`Order history`}
      action={
        <Button onClick={() => navigate(`/customers/view/${id}`)}>Back to customer</Button>
      }
    >
      {loading ? (
        <p style={{ padding: 40, textAlign: 'center', color: '#6b7280' }}>Loading orders…</p>
      ) : (
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f9fafb' }}>
                <th style={th}>Order</th>
                <th style={th}>Date</th>
                <th style={th}>Items</th>
                <th style={th}>Status</th>
                <th style={{ ...th, textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {orders.length > 0 ? (
                orders.map(order => {
                  const dateStr = order.orderDate || order.order_date;
                  const ref = order.order_number || order.id;
                  return (
                    <tr key={order.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={td}>
                        <Link to={`/orders/view/${order.id}`} style={{ color: '#0074d9', fontWeight: 600, textDecoration: 'none' }}>
                          #{ref}
                        </Link>
                        {order.has_backordered && (
                          <Tag color="warning" style={{ marginLeft: 8, fontSize: 11 }}>Backorder</Tag>
                        )}
                      </td>
                      <td style={td}>{dateStr ? new Date(dateStr).toLocaleDateString('en-GB') : '—'}</td>
                      <td style={{ ...td, fontSize: 13, color: '#6b7280' }}>
                        {(order.items || []).slice(0, 3).map((item, i) => {
                          const qty = item.qty ?? item.quantity ?? 0;
                          const name = item.productName || item.product_name || 'Item';
                          return <div key={i}>{qty} × {name}</div>;
                        })}
                        {(order.items || []).length > 3 && (
                          <div>+{(order.items || []).length - 3} more</div>
                        )}
                      </td>
                      <td style={td}>
                        <Tag color={statusColor(order.status)}>{order.status}</Tag>
                      </td>
                      <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>£{Number(order.total || 0).toFixed(2)}</td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} style={{ ...td, textAlign: 'center', color: '#9ca3af' }}>No orders found for this customer.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </PageLayout>
  );
};

const th: React.CSSProperties = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: '#6b7280',
};

const td: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 14,
  color: '#111827',
  verticalAlign: 'top',
};

export default CustomerOrders;
